import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, useTheme } from '@mui/material';
import {
  SearchOff,
  EventBusy,
  ChatBubbleOutline,
  NotificationsNone,
  ErrorOutline,
  LockOutlined,
  EmojiEmotions,
} from '@mui/icons-material';

/**
 * EmptyState - Premium empty state component with illustrations and CTAs
 * 
 * Use when:
 * - No search/filter results
 * - Empty list (appointments, messages, etc.)
 * - Error states
 * - First-time user experiences
 * - Permission denied
 * 
 * Features:
 * - 7+ built-in variants
 * - Custom icon support
 * - Optional CTA button
 * - Responsive layout
 * - Brand-aligned styling
 * - Empathetic copy
 * 
 * Props:
 * - variant: 'noResults' | 'noAppointments' | 'noMessages' | 'noNotifications' | 'error' | 'noPermission' | 'firstTime' | 'custom'
 * - icon: ReactNode (custom icon, overrides variant icon)
 * - title: string (custom title, overrides variant title)
 * - description: string (custom description, overrides variant description)
 * - action: { label: string, onClick: function, variant?: 'primary'|'secondary'|'text' } (optional CTA)
 * - size: 'small' | 'medium' | 'large' (default: 'medium')
 * - illustration: 'icon' | 'svg' | 'lottie' (future: support SVG/Lottie)
 * 
 * Example:
 * ```jsx
 * // Built-in variant
 * <EmptyState variant="noResults" action={{ label: "Limpiar filtros", onClick: clearFilters }} />
 * 
 * // Custom
 * <EmptyState
 *   icon={<CustomIcon />}
 *   title="Título personalizado"
 *   description="Descripción personalizada"
 *   action={{ label: "Acción", onClick: handleAction }}
 * />
 * ```
 * 
 * @version 1.0.0 (Feb 2026)
 */

// Icon mappings for variants
const VARIANT_ICONS = {
  noResults: SearchOff,
  noAppointments: EventBusy,
  noMessages: ChatBubbleOutline,
  noNotifications: NotificationsNone,
  error: ErrorOutline,
  noPermission: LockOutlined,
  firstTime: EmojiEmotions,
};

// Text content for variants
const VARIANT_CONTENT = {
  noResults: {
    title: 'No encontramos resultados',
    description: 'Intenta ajustar los filtros o buscar con otros términos',
  },
  noAppointments: {
    title: 'No tienes citas programadas',
    description: 'Agenda tu primera sesión con un terapeuta profesional certificado',
  },
  noMessages: {
    title: 'No hay conversaciones',
    description: 'Los mensajes con tus terapeutas aparecerán aquí',
  },
  noNotifications: {
    title: '¡Todo al día!',
    description: 'No tienes notificaciones nuevas en este momento',
  },
  error: {
    title: 'Algo salió mal',
    description: 'No pudimos cargar el contenido. Por favor intenta de nuevo más tarde.',
  },
  noPermission: {
    title: 'Acceso restringido',
    description: 'No tienes los permisos necesarios para ver este contenido',
  },
  firstTime: {
    title: '¡Bienvenido a Terappy!',
    description: 'Comienza tu viaje de bienestar mental con profesionales certificados',
  },
};

// Size configurations
const SIZE_CONFIG = {
  small: {
    iconSize: 64,
    titleVariant: 'h6',
    spacing: 2,
    maxWidth: 320,
  },
  medium: {
    iconSize: 96,
    titleVariant: 'h5',
    spacing: 3,
    maxWidth: 400,
  },
  large: {
    iconSize: 128,
    titleVariant: 'h4',
    spacing: 4,
    maxWidth: 480,
  },
};

const EmptyState = ({
  variant = 'noResults',
  icon: customIcon,
  title: customTitle,
  description: customDescription,
  action,
  size = 'medium',
  illustration = 'icon',
}) => {
  const theme = useTheme();
  const config = SIZE_CONFIG[size];

  // Get variant content
  const variantContent = VARIANT_CONTENT[variant] || VARIANT_CONTENT.noResults;
  const title = customTitle || variantContent.title;
  const description = customDescription || variantContent.description;

  // Get icon
  const IconComponent = VARIANT_ICONS[variant] || SearchOff;
  const iconElement = customIcon || (
    <IconComponent
      sx={{
        fontSize: config.iconSize,
        color: 'grey.400',
      }}
    />
  );

  // Determine if this is a positive state (no action needed)
  const isPositive = variant === 'noNotifications';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: config.spacing * 2,
        px: 3,
        maxWidth: config.maxWidth,
        mx: 'auto',
      }}
    >
      {/* Icon/Illustration */}
      <Box
        sx={{
          mb: config.spacing,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: config.iconSize,
          height: config.iconSize,
          borderRadius: '50%',
          bgcolor: isPositive ? 'success.light' : 'grey.100',
          opacity: 0.8,
        }}
      >
        {iconElement}
      </Box>

      {/* Title */}
      <Typography
        variant={config.titleVariant}
        fontFamily={theme.typography.fontFamilyHeading}
        fontWeight={700}
        color="text.primary"
        sx={{ mb: 1.5 }}
      >
        {title}
      </Typography>

      {/* Description */}
      {description && (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: action ? config.spacing : 0,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      )}

      {/* Action Button */}
      {action && (
        <Button
          variant={action.variant || 'contained'}
          color="primary"
          size={size === 'large' ? 'large' : 'medium'}
          onClick={action.onClick}
          sx={{
            minWidth: 160,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
};

EmptyState.propTypes = {
  variant: PropTypes.oneOf([
    'noResults',
    'noAppointments',
    'noMessages',
    'noNotifications',
    'error',
    'noPermission',
    'firstTime',
    'custom',
  ]),
  icon: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  }),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  illustration: PropTypes.oneOf(['icon', 'svg', 'lottie']),
};

export default EmptyState;
